// src/modules/profiles.js � Profile picker, PIN, management, inactivity
import { state } from '../state.js';
import { AUTO_LOCK_MINUTES } from '../utils/constants.js';
import { apiFetch } from '../utils/api.js';
import { escapeHtml, showView, showConfirmDialog, showToast, navigateTo, closeSidebar } from '../utils/dom.js';
import { logger, apiFetchWithRefresh } from './debug.js';
import { ProfileStore } from '../stores/profile-store.js';
import { updateTrialBanner, trialBannerState } from './trial.js';
import { getCachedTrialState } from './auth.js';

// Crypto helper: SHA-256 hash a PIN for server verification
export async function hashPin(pin) {
    const encoder = new TextEncoder();
    const data = encoder.encode(pin);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}


// Avoids circular imports for functions that live in main.js
export const _deps = {
    proceedToApp: () => {},
    checkProfilesAndProceed: () => {},
};

export function renderProfilePicker(profiles) {
    const grid = document.getElementById('profileGrid');
    let html = profiles.map(p => {
        const initial = (p.displayName || '?').charAt(0).toUpperCase();
        const role = (p.role || '').replace('_', ' ');
        return `<div class="profile-card" data-action="select-profile" data-id="${p.profileId}">
            <div class="profile-card-avatar" style="background:${p.color || '#4A90D9'}">${initial}</div>
            <div class="profile-card-name">${escapeHtml(p.displayName)}</div>
            <div class="profile-card-role">${escapeHtml(role)}</div>
        </div>`;
    }).join('');

    // Add "Add Profile" card — only show if no profile active yet (initial picker) or admin profile switching
    if (!currentProfileId || isProfileAdmin) {
        html += `<div class="profile-card profile-card-add" data-action="open-profile-mgmt">
            <div class="profile-card-avatar" style="background:var(--primary-light)">+</div>
            <div class="profile-card-name">Add Profile</div>
        </div>`;
    }

    grid.innerHTML = html;
}

export function selectProfile(profileId) {
    const profile = profilesData && profilesData.profiles
        ? profilesData.profiles.find(p => p.profileId === profileId)
        : null;
    if (!profile) return;

    // ALL profiles ALWAYS require PIN (mandatory)
    showPinModal(profile);
}

// ============================================================================
// PROVIDER PROFILES — PIN Entry Modal
// ============================================================================

let pinTargetProfile = null;

export function showPinModal(profile) {
    pinTargetProfile = profile;
    const initial = (profile.displayName || '?').charAt(0).toUpperCase();

    document.getElementById('pinAvatar').style.background = profile.color || '#4A90D9';
    document.getElementById('pinAvatar').textContent = initial;
    document.getElementById('pinProfileName').textContent = profile.displayName || '';
    document.getElementById('pinProfileRole').textContent = (profile.role || '').replace('_', ' ');
    document.getElementById('pinStatus').textContent = '';
    document.getElementById('pinStatus').className = 'pin-status';

    updateTrialBanner();

    // Clear and enable PIN inputs
    const pins = [document.getElementById('pin1'), document.getElementById('pin2'),
                  document.getElementById('pin3'), document.getElementById('pin4')];
    pins.forEach(p => { p.value = ''; p.dataset.digit = ''; p.disabled = false; p.classList.remove('error', 'success'); });

    // Trial expired: block PIN entry at the modal (preserves session; admin must extend the trial).
    const trial = getCachedTrialState();
    if (trialBannerState(trial.status, trial.trialEndDate).level === 'expired') {
        const status = document.getElementById('pinStatus');
        status.textContent = 'Your trial has ended — contact your administrator to continue.';
        status.className = 'pin-status error';
        pins.forEach(p => { p.disabled = true; });
    }

    document.getElementById('pinModal').classList.add('active');

    // Focus first PIN input using double rAF for reliable mobile focus
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            pins[0].focus({ preventScroll: false });
        });
    });

    // Setup PIN input handlers
    setupPinInputHandlers('pin', onPinComplete);
}

export function closePinModal() {
    document.getElementById('pinModal').classList.remove('active');
    pinTargetProfile = null;
}

export function setupPinInputHandlers(prefix, onComplete) {
    const inputs = [
        document.getElementById(prefix + '1'),
        document.getElementById(prefix + '2'),
        document.getElementById(prefix + '3'),
        document.getElementById(prefix + '4'),
    ];
    // Store real digits separately — input.value shows '•' for instant masking
    const digits = ['', '', '', ''];

    inputs.forEach((input, idx) => {
        // Remove old listeners by cloning
        const newInput = input.cloneNode(true);
        input.parentNode.replaceChild(newInput, input);
        inputs[idx] = newInput;
        newInput.dataset.digit = '';

        newInput.addEventListener('input', (e) => {
            const raw = e.target.value.replace(/[^0-9\u2022]/g, '');
            // Extract the actual digit (not the dot)
            const digit = raw.replace(/\u2022/g, '').slice(0, 1);
            if (digit) {
                digits[idx] = digit;
                newInput.dataset.digit = digit;
                // Immediately show dot — no brief flash of the number
                e.target.value = '\u2022';
                if (idx < 3) {
                    inputs[idx + 1].focus();
                }
            } else {
                digits[idx] = '';
                newInput.dataset.digit = '';
                e.target.value = '';
            }
            // Check if all 4 digits entered
            if (idx === 3 && digits[3]) {
                const pin = digits.join('');
                if (pin.length === 4) {
                    onComplete(pin);
                }
            }
        });

        newInput.addEventListener('keydown', (e) => {
            if (e.key === 'Backspace') {
                if (digits[idx]) {
                    // Clear current
                    digits[idx] = '';
                    newInput.dataset.digit = '';
                    newInput.value = '';
                    e.preventDefault();
                } else if (idx > 0) {
                    // Move back
                    digits[idx - 1] = '';
                    inputs[idx - 1].dataset.digit = '';
                    inputs[idx - 1].value = '';
                    inputs[idx - 1].focus();
                    e.preventDefault();
                }
            }
        });

        // Handle paste
        newInput.addEventListener('paste', (e) => {
            e.preventDefault();
            const pasted = (e.clipboardData.getData('text') || '').replace(/[^0-9]/g, '').slice(0, 4);
            for (let i = 0; i < pasted.length && (idx + i) < 4; i++) {
                digits[idx + i] = pasted[i];
                inputs[idx + i].dataset.digit = pasted[i];
                inputs[idx + i].value = '\u2022';
            }
            if (pasted.length >= (4 - idx)) {
                const pin = digits.join('');
                if (pin.length === 4) {
                    onComplete(pin);
                }
            } else if (idx + pasted.length < 4) {
                inputs[idx + pasted.length].focus();
            }
        });
    });

    // Expose a helper to read PIN value (for forms that read .value directly)
    inputs._getPin = () => digits.join('');
}

export async function onPinComplete(pin) {
    if (!pinTargetProfile) return;

    const statusEl = document.getElementById('pinStatus');
    const pins = [document.getElementById('pin1'), document.getElementById('pin2'),
                  document.getElementById('pin3'), document.getElementById('pin4')];

    statusEl.textContent = 'Verifying...';
    statusEl.className = 'pin-status verifying';
    pins.forEach(p => p.disabled = true);

    try {
        const res = await apiFetch('/profiles/' + pinTargetProfile.profileId + '/verify-pin', {
            method: 'POST',
            body: JSON.stringify({ pinHash: await hashPin(pin) }),
        });
        const data = await res.json();

        if (data.valid) {
            // Success
            pins.forEach(p => p.classList.add('success'));
            statusEl.textContent = '';

            // Store profile session
            currentProfileId = pinTargetProfile.profileId;
            currentProfileDisplayName = data.displayName || pinTargetProfile.displayName;
            currentProfileColor = pinTargetProfile.color || '#4A90D9';
            isProfileAdmin = pinTargetProfile.isDefault === true; // First profile = admin
            sessionStorage.setItem('dentscribe_profileId', currentProfileId);
            sessionStorage.setItem('dentscribe_profileDisplayName', currentProfileDisplayName);
            sessionStorage.setItem('dentscribe_profileColor', currentProfileColor);
            sessionStorage.setItem('dentscribe_isProfileAdmin', isProfileAdmin ? '1' : '0');

            logger.info('PROFILES', 'PIN verified, profile activated', { profileId: currentProfileId, name: currentProfileDisplayName });

            setTimeout(() => {
                closePinModal();
                _deps.proceedToApp();
                // Apply default appointment type from profile preferences
                applyProfileDefaultAppointmentType(currentProfileId);
            }, 400);
        } else if (data.locked) {
            // Lockout disabled — just show wrong PIN message
            statusEl.textContent = 'Incorrect PIN. Please try again.';
            statusEl.className = 'pin-status error';
            pins.forEach(p => { p.classList.add('error'); p.disabled = false; });

            // Shake and clear after animation
            setTimeout(() => {
                pins.forEach(p => { p.value = ''; p.dataset.digit = ''; p.classList.remove('error'); });
                pins[0].focus();
            }, 600);
        } else {
            // Wrong PIN — unlimited retries
            statusEl.textContent = 'Incorrect PIN. Please try again.';
            statusEl.className = 'pin-status error';
            pins.forEach(p => { p.classList.add('error'); p.disabled = false; });

            // Shake and clear after animation
            setTimeout(() => {
                pins.forEach(p => { p.value = ''; p.dataset.digit = ''; p.classList.remove('error'); });
                pins[0].focus();
            }, 600);
        }
    } catch (err) {
        logger.error('PROFILES', 'PIN verification failed (network)', err);
        statusEl.textContent = 'Network error. Please try again.';
        statusEl.className = 'pin-status error';
        pins.forEach(p => p.disabled = false);
    }
}

// ============================================================================
// PROVIDER PROFILES — Profile Badge & Switch
// ============================================================================

export function updateProfileBadge() {
    const badge = document.getElementById('profileBadge');
    const name = ProfileStore.get('currentDisplayName');
    const color = ProfileStore.get('currentColor') || '#4A90D9';
    const id = ProfileStore.get('currentProfileId');
    if (id && name) {
        if (badge) {
            badge.classList.remove('hidden');
            var dot = document.getElementById('profileBadgeDot');
            var nameEl = document.getElementById('profileBadgeName');
            if (dot) dot.style.background = color;
            if (nameEl) nameEl.textContent = name;
        }
    } else {
        if (badge) badge.classList.add('hidden');
    }
}

export function toggleHeaderMenu() {
    // Legacy: toggle sidebar on mobile (same as toggleSidebar for unified nav)
    toggleSidebar();
}

export function closeHeaderMenu() {
    // Legacy: close sidebar on mobile
    closeSidebar();
}

// Keep as alias for backward compatibility
export function toggleProfileMenu() {
    toggleSidebar();
}

// Legacy sidebar functions — now point to the unified sidebar
// (old code passed page names like 'admin', 'analytics', 'profile')

export function switchProfile() {
    closeHeaderMenu();
    clearProfileSession();
    stopInactivityTracker();
    // Re-fetch profiles and show picker
    _deps.checkProfilesAndProceed();
}

export function clearProfileSession() {
    ProfileStore.clear();
}

// ============================================================================
// PROVIDER PROFILES — Auto-Lock on Inactivity
// ============================================================================

let inactivityTimer = null;
let lastInteractionTime = Date.now();

export function startInactivityTracker() {
    stopInactivityTracker();
    lastInteractionTime = Date.now();

    const events = ['click', 'keypress', 'touchstart', 'mousemove'];
    events.forEach(evt => document.addEventListener(evt, resetInactivityTimer, { passive: true }));

    inactivityTimer = setInterval(checkInactivity, 30000); // Check every 30s
}

export function stopInactivityTracker() {
    if (inactivityTimer) {
        clearInterval(inactivityTimer);
        inactivityTimer = null;
    }
    const events = ['click', 'keypress', 'touchstart', 'mousemove'];
    events.forEach(evt => document.removeEventListener(evt, resetInactivityTimer));
}

export function resetInactivityTimer() {
    lastInteractionTime = Date.now();
}

export function checkInactivity() {
    if (!currentProfileId) return; // No profile active — nothing to lock
    // v2.11.0 FIX C: NEVER auto-lock during an active recording session (recording or paused)
    if (state.isRecording || state.isPaused) return;
    const elapsed = Date.now() - lastInteractionTime;
    const lockAfterMs = AUTO_LOCK_MINUTES * 60 * 1000;

    if (elapsed >= lockAfterMs) {
        logger.info('PROFILES', 'Auto-lock triggered due to inactivity', { elapsedMin: Math.round(elapsed / 60000) });
        clearProfileSession();
        stopInactivityTracker();
        updateProfileBadge();
        // Show profile picker
        if (profilesData && profilesData.profiles && profilesData.profiles.length > 0) {
            renderProfilePicker(profilesData.profiles);
            showView('view-profiles');
        }
    }
}

// ============================================================================
// PROVIDER PROFILES — Profile Management (Admin)
// ============================================================================

let editingProfileId = null;
let resetPinProfileId = null;
let resetPinOldVerified = false;

export function openProfileMgmt() {
    const profileMenu = document.getElementById('profileMenu');
    if (profileMenu) profileMenu.classList.add('hidden');
    closeHeaderMenu();
    hideProfileForm();
    hideResetPinForm();
    loadProfileMgmtList();
    document.getElementById('profileMgmtModal').classList.add('active');
}

export function closeProfileMgmt() {
    document.getElementById('profileMgmtModal').classList.remove('active');
    editingProfileId = null;
}

export async function loadProfileMgmtList() {
    const container = document.getElementById('profileMgmtList');
    container.innerHTML = '<div class="empty-state"><p>Loading...</p></div>';

    try {
        const res = await apiFetch('/profiles');
        if (!res.ok) throw new Error('Failed to load profiles');
        const data = await res.json();
        profilesData = data;
        const profiles = data.profiles || [];

        if (profiles.length === 0) {
            container.innerHTML = '<div class="empty-state"><p>No profiles yet. Create one to get started.</p></div>';
            return;
        }

        // FIX N4: inline onclick with interpolated server data replaced by
        // dataset + a single delegated listener. escapeHtml() cannot protect a
        // JS string literal inside an HTML attribute, so the pattern had to go.
        container.innerHTML = profiles.map(p => {
            const initial = (p.displayName || '?').charAt(0).toUpperCase();
            const role = (p.role || '').replace('_', ' ');
            const safeColor = /^#[0-9a-fA-F]{3,8}$/.test(p.color || '') ? p.color : '#4A90D9';
            return `<div class="profile-mgmt-item">
                <div class="profile-mgmt-avatar" style="background:${safeColor}">${escapeHtml(initial)}</div>
                <div class="profile-mgmt-info">
                    <div class="profile-mgmt-name">${escapeHtml(p.displayName)}</div>
                    <div class="profile-mgmt-role">${escapeHtml(role)}</div>
                </div>
                <div class="profile-mgmt-item-actions">
                    <button class="btn-mgmt-edit" data-profile-action="edit" data-profile-id="${escapeHtml(p.profileId)}">Edit</button>
                    <button class="btn-mgmt-pin" data-profile-action="pin" data-profile-id="${escapeHtml(p.profileId)}" data-profile-name="${escapeHtml(p.displayName)}">PIN</button>
                    <button class="btn-mgmt-delete" data-profile-action="delete" data-profile-id="${escapeHtml(p.profileId)}" data-profile-name="${escapeHtml(p.displayName)}">Delete</button>
                </div>
            </div>`;
        }).join('');

        // FIX N4: one delegated handler, no code built from server data.
        container.onclick = (event) => {
            const btn = event.target.closest('[data-profile-action]');
            if (!btn || !container.contains(btn)) return;
            const id = btn.dataset.profileId;
            const name = btn.dataset.profileName || '';
            if (btn.dataset.profileAction === 'edit') editProfile(id);
            else if (btn.dataset.profileAction === 'pin') showResetPin(id, name);
            else if (btn.dataset.profileAction === 'delete') deleteProfile(id, name);
        };
    } catch (err) {
        container.innerHTML = '<div class="empty-state"><p>Error loading profiles.</p></div>';
        logger.error('PROFILES', 'Failed to load profile list', err);
    }

    // Show migrate button for admin
    const migrateContainer = document.getElementById('migrateNotesContainer');
    if (migrateContainer) {
        if (isProfileAdmin) {
            migrateContainer.innerHTML = '<button class="btn-migrate" data-action="migrate-old-notes">Migrate Old Notes</button>';
            migrateContainer.classList.remove('hidden');
        } else {
            migrateContainer.classList.add('hidden');
        }
    }
}

export async function migrateOldNotes() {
    var confirmed = await showConfirmDialog('Migrate Notes', 'This will move all existing notes to the admin profile. Continue?', 'Continue', 'btn-primary');
    if (!confirmed) return;
    try {
        const res = await apiFetch('/profiles/migrate-notes', { method: 'POST' });
        const data = await res.json();
        if (res.ok) {
            showToast('Migrated ' + data.migrated + ' notes to admin profile.', 'success');
        } else {
            showToast('Migration failed: ' + (data.error || 'Unknown error'), 'error');
        }
    } catch (err) {
        showToast('Migration failed: ' + err.message, 'error');
    }
}

export function showCreateProfileForm() {
    editingProfileId = null;
    document.getElementById('profileFormTitle').textContent = 'Create Profile';
    document.getElementById('pfSubmitBtn').textContent = 'Create';
    document.getElementById('pfName').value = '';
    document.getElementById('pfRole').value = 'dentist';
    document.getElementById('pfPinGroup').classList.remove('hidden');
    clearFormPins('pfPin');
    resetColorPicker('#4A90D9');
    document.getElementById('profileFormError').classList.add('hidden');
    document.getElementById('profileForm').classList.remove('hidden');
    document.getElementById('resetPinForm').classList.add('hidden');

    // Hide Note Preferences on create (only available on edit)
    var notePrefsGroup = document.getElementById('pfNotePrefsGroup');
    if (notePrefsGroup) notePrefsGroup.classList.add('hidden');

    // Setup PIN input handlers for form
    setupPinInputHandlers('pfPin', () => {});
}

export function editProfile(profileId) {
    const profile = profilesData && profilesData.profiles
        ? profilesData.profiles.find(p => p.profileId === profileId)
        : null;
    if (!profile) return;

    editingProfileId = profileId;
    document.getElementById('profileFormTitle').textContent = 'Edit Profile';
    document.getElementById('pfSubmitBtn').textContent = 'Save';
    document.getElementById('pfName').value = profile.displayName || '';
    document.getElementById('pfRole').value = profile.role || 'dentist';
    document.getElementById('pfPinGroup').classList.add('hidden'); // Don't show PIN on edit
    resetColorPicker(profile.color || '#4A90D9');
    document.getElementById('profileFormError').classList.add('hidden');
    document.getElementById('profileForm').classList.remove('hidden');
    document.getElementById('resetPinForm').classList.add('hidden');

    // Show and populate Note Preferences section
    var notePrefsGroup = document.getElementById('pfNotePrefsGroup');
    if (notePrefsGroup) {
        notePrefsGroup.classList.remove('hidden');
        populateNotePreferences(profile);
    }
}

export function hideProfileForm() {
    document.getElementById('profileForm').classList.add('hidden');
    editingProfileId = null;
    // Reset note preferences section (complete state reset per code-patterns)
    var notePrefsGroup = document.getElementById('pfNotePrefsGroup');
    if (notePrefsGroup) notePrefsGroup.classList.add('hidden');
    var formatList = document.getElementById('pfFormatPrefsList');
    if (formatList) formatList.innerHTML = '';
}

export function resetColorPicker(selectedColor) {
    document.querySelectorAll('#pfColorPicker .color-swatch').forEach(swatch => {
        swatch.style.background = swatch.dataset.color;
        if (swatch.dataset.color === selectedColor) {
            swatch.classList.add('selected');
        } else {
            swatch.classList.remove('selected');
        }
    });
}

export function clearFormPins(prefix) {
    for (let i = 1; i <= 4; i++) {
        const el = document.getElementById(prefix + i);
        if (el) { el.value = ''; el.dataset.digit = ''; }
    }
}

// ============================================================================
// NOTE PREFERENCES — Populate/collect preferences in profile edit form
// ============================================================================

/**
 * Populate the Note Preferences section when editing a profile.
 * Shows the default appointment type dropdown and per-type format dropdowns.
 */
export function populateNotePreferences(profile) {
    var prefs = (profile && profile.notePreferences) || {};
    var defaultType = prefs.defaultAppointmentType || 'other_general';
    var formatPrefs = prefs.formatPreferences || {};

    // Set default appointment type dropdown
    var defaultDropdown = document.getElementById('pfDefaultApptType');
    if (defaultDropdown) {
        defaultDropdown.value = defaultType;
        // If value didn't match (invalid stored value), fall back to other_general
        if (defaultDropdown.value !== defaultType) {
            defaultDropdown.value = 'other_general';
        }
    }

    // Render per-type format preference dropdowns
    var listContainer = document.getElementById('pfFormatPrefsList');
    if (!listContainer) return;
    listContainer.innerHTML = '';

    // Show all appointment types with their current format preference
    state.APPOINTMENT_TYPES.forEach(function(typeObj) {
        var currentFormat = formatPrefs[typeObj.key] || 'standard';
        var row = document.createElement('div');
        row.className = 'format-pref-row';

        var label = document.createElement('span');
        label.className = 'format-pref-label';
        label.textContent = typeObj.shortLabel;

        var select = document.createElement('select');
        select.className = 'format-pref-select';
        select.dataset.typeKey = typeObj.key;
        select.setAttribute('aria-label', 'Format preference for ' + typeObj.label);

        var optBrief = document.createElement('option');
        optBrief.value = 'brief';
        optBrief.textContent = 'Brief';
        var optStandard = document.createElement('option');
        optStandard.value = 'standard';
        optStandard.textContent = 'Standard';
        var optDetailed = document.createElement('option');
        optDetailed.value = 'detailed';
        optDetailed.textContent = 'Detailed';

        select.appendChild(optBrief);
        select.appendChild(optStandard);
        select.appendChild(optDetailed);
        select.value = currentFormat;

        row.appendChild(label);
        row.appendChild(select);
        listContainer.appendChild(row);
    });
}

/**
 * Collect note preferences from the profile edit form.
 * Returns a notePreferences object or null if the section is hidden.
 */
export function collectNotePreferences() {
    var notePrefsGroup = document.getElementById('pfNotePrefsGroup');
    if (!notePrefsGroup || notePrefsGroup.classList.contains('hidden')) return null;

    var defaultDropdown = document.getElementById('pfDefaultApptType');
    var defaultType = defaultDropdown ? defaultDropdown.value : 'other_general';

    // Validate defaultType against state.APPOINTMENT_TYPES
    if (!state.APPOINTMENT_TYPES.find(function(t) { return t.key === defaultType; })) {
        defaultType = 'other_general';
    }

    // Collect per-type format preferences
    var formatPreferences = {};
    var selects = document.querySelectorAll('#pfFormatPrefsList .format-pref-select');
    selects.forEach(function(sel) {
        var typeKey = sel.dataset.typeKey;
        var val = sel.value;
        // Only store non-standard values to keep the object lean
        if (val && val !== 'standard' && typeKey) {
            formatPreferences[typeKey] = val;
        }
    });

    return {
        defaultAppointmentType: defaultType,
        formatPreferences: formatPreferences
    };
}

/**
 * After PIN login succeeds, check if the profile has a defaultAppointmentType
 * and auto-select it on the Record page.
 */
export function applyProfileDefaultAppointmentType(profileId) {
    if (!profilesData || !profilesData.profiles) return;
    var profile = profilesData.profiles.find(function(p) { return p.profileId === profileId; });
    if (!profile || !profile.notePreferences) return;

    var defaultType = profile.notePreferences.defaultAppointmentType;
    if (!defaultType || defaultType === 'other_general') return;

    // Validate the type exists
    if (!state.APPOINTMENT_TYPES.find(function(t) { return t.key === defaultType; })) return;

    // Only apply if user hasn't already manually selected a type this session
    var currentSessionType = sessionStorage.getItem('dentscribe_appt_type');
    if (currentSessionType && currentSessionType !== 'other_general') return;

    // Apply the default
    selectAppointmentType(defaultType);
    logger.info('PROFILES', 'Applied default appointment type from preferences', { type: defaultType });
}

export async function submitProfileForm() {
    const name = document.getElementById('pfName').value.trim();
    const role = document.getElementById('pfRole').value;
    const colorEl = document.querySelector('#pfColorPicker .color-swatch.selected');
    const color = colorEl ? colorEl.dataset.color : '#4A90D9';
    const errorEl = document.getElementById('profileFormError');

    if (!name) {
        errorEl.textContent = 'Please enter a display name.';
        errorEl.classList.remove('hidden');
        return;
    }

    if (editingProfileId) {
        // Update existing profile
        const submitBtn = document.getElementById('pfSubmitBtn');
        const originalBtnText = submitBtn.textContent;
        submitBtn.disabled = true;
        submitBtn.textContent = 'Saving...';
        try {
            var body = { displayName: name, role: role, color: color };
            // Collect note preferences if the section is visible
            var notePrefs = collectNotePreferences();
            if (notePrefs) {
                body.notePreferences = notePrefs;
            }
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000);
            const res = await apiFetch('/profiles/' + editingProfileId, {
                method: 'PATCH',
                body: JSON.stringify(body),
                signal: controller.signal,
            });
            clearTimeout(timeoutId);
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'Failed to update profile');
            }
            hideProfileForm();
            loadProfileMgmtList();
            // Refresh store from server to keep all subscribers current
            const refreshRes = await apiFetch('/profiles');
            if (refreshRes.ok) {
                profilesData = await refreshRes.json();
            }
            logger.info('PROFILES', 'Profile updated with preferences', { profileId: editingProfileId });
        } catch (err) {
            const msg = err.name === 'AbortError' ? 'Request timed out. Please try again.' : err.message;
            errorEl.textContent = msg;
            errorEl.classList.remove('hidden');
        } finally {
            submitBtn.disabled = false;
            submitBtn.textContent = originalBtnText;
        }
    } else {
        // Create new profile
        const isFirstProfile = !profilesData || !profilesData.profiles || profilesData.profiles.length === 0;
        const pin = [1,2,3,4].map(i => document.getElementById('pfPin' + i).dataset.digit || '').join('');

        // PIN required for doc profiles, optional for admin (first profile)
        if (!isFirstProfile && (pin.length !== 4 || !/^\d{4}$/.test(pin))) {
            errorEl.textContent = 'Please enter a valid 4-digit PIN.';
            errorEl.classList.remove('hidden');
            return;
        }

        try {
            const body = { displayName: name, role, color };
            if (pin.length === 4 && /^\d{4}$/.test(pin)) {
                body.pinHash = await hashPin(pin);
            }
            const res = await apiFetch('/profiles', {
                method: 'POST',
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'Failed to create profile');
            }
            hideProfileForm();
            loadProfileMgmtList();
            // Refresh store from server to keep all subscribers current
            const refreshRes2 = await apiFetch('/profiles');
            if (refreshRes2.ok) {
                profilesData = await refreshRes2.json();
            }
            logger.info('PROFILES', 'Profile created', { name });
        } catch (err) {
            errorEl.textContent = err.message;
            errorEl.classList.remove('hidden');
        }
    }
}

export async function deleteProfile(profileId, displayName) {
    var confirmed = await showConfirmDialog('Delete Profile', 'Delete profile "' + displayName + '"? Notes will be preserved but the profile will be removed.', 'Delete', 'btn-danger');
    if (!confirmed) return;

    try {
        const res = await apiFetch('/profiles/' + profileId, { method: 'DELETE' });
        if (!res.ok) throw new Error('Failed to delete profile');

        // If the deleted profile is currently active, clear it
        if (currentProfileId === profileId) {
            clearProfileSession();
            updateProfileBadge();
        }
        loadProfileMgmtList();
        // Refresh store from server to keep all subscribers current
        const refreshRes = await apiFetch('/profiles');
        if (refreshRes.ok) {
            profilesData = await refreshRes.json();
        }
        logger.info('PROFILES', 'Profile deleted', { profileId, displayName });
    } catch (err) {
        showToast('Error deleting profile: ' + err.message, 'error');
    }
}

// --- Reset PIN ---
export function showResetPin(profileId, displayName) {
    resetPinProfileId = profileId;
    resetPinOldVerified = false;
    document.getElementById('resetPinName').textContent = displayName;
    clearFormPins('rpOldPin');
    clearFormPins('rpPin');
    document.getElementById('resetPinError').classList.add('hidden');
    document.getElementById('resetPinOldSection').classList.remove('hidden');
    document.getElementById('resetPinNewSection').classList.add('hidden');
    document.getElementById('resetPinForm').classList.remove('hidden');
    document.getElementById('profileForm').classList.add('hidden');

    setupPinInputHandlers('rpOldPin', () => {});
}

export function hideResetPinForm() {
    document.getElementById('resetPinForm').classList.add('hidden');
    resetPinProfileId = null;
    resetPinOldVerified = false;
}

export async function verifyOldPin() {
    const pin = [1,2,3,4].map(i => document.getElementById('rpOldPin' + i).dataset.digit || '').join('');
    const errorEl = document.getElementById('resetPinError');

    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
        errorEl.textContent = 'Please enter your current 4-digit PIN.';
        errorEl.classList.remove('hidden');
        return;
    }

    errorEl.classList.add('hidden');

    try {
        const res = await apiFetchWithRefresh('/profiles/' + resetPinProfileId + '/verify-pin', {
            method: 'POST',
            body: JSON.stringify({ pinHash: await hashPin(pin) }),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || 'Incorrect PIN');
        }
        // Old PIN verified — show new PIN section
        resetPinOldVerified = true;
        document.getElementById('resetPinOldSection').classList.add('hidden');
        document.getElementById('resetPinNewSection').classList.remove('hidden');
        clearFormPins('rpPin');
        setupPinInputHandlers('rpPin', () => {});
    } catch (err) {
        errorEl.textContent = err.message;
        errorEl.classList.remove('hidden');
    }
}

export async function submitResetPin() {
    const errorEl = document.getElementById('resetPinError');

    if (!resetPinOldVerified) {
        errorEl.textContent = 'Please verify your current PIN first.';
        errorEl.classList.remove('hidden');
        return;
    }

    const pin = [1,2,3,4].map(i => document.getElementById('rpPin' + i).dataset.digit || '').join('');

    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
        errorEl.textContent = 'Please enter a valid 4-digit PIN.';
        errorEl.classList.remove('hidden');
        return;
    }

    try {
        const res = await apiFetchWithRefresh('/profiles/' + resetPinProfileId + '/reset-pin', {
            method: 'POST',
            body: JSON.stringify({ newPinHash: await hashPin(pin) }),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || 'Failed to reset PIN');
        }
        hideResetPinForm();
        logger.info('PROFILES', 'PIN reset successful', { profileId: resetPinProfileId });
        showToast('PIN has been reset successfully.', 'success');
    } catch (err) {
        errorEl.textContent = err.message;
        errorEl.classList.remove('hidden');
    }
}

// ============================================================================
// PROVIDER PROFILES — Color Picker Event Delegation
// ============================================================================

document.addEventListener('DOMContentLoaded', () => {
    // Color swatch selection
    const colorPicker = document.getElementById('pfColorPicker');
    if (colorPicker) {
        colorPicker.addEventListener('click', (e) => {
            const swatch = e.target.closest('.color-swatch');
            if (!swatch) return;
            document.querySelectorAll('#pfColorPicker .color-swatch').forEach(s => s.classList.remove('selected'));
            swatch.classList.add('selected');
        });

        // Initialize color swatches with their background colors
        document.querySelectorAll('#pfColorPicker .color-swatch').forEach(swatch => {
            swatch.style.background = swatch.dataset.color;
        });
    }

    // PIN modal close on backdrop
    const pinModal = document.getElementById('pinModal');
    if (pinModal) pinModal.addEventListener('click', (e) => { if (e.target === e.currentTarget) closePinModal(); });

    // Profile management modal close on backdrop
    const mgmtModal = document.getElementById('profileMgmtModal');
    if (mgmtModal) mgmtModal.addEventListener('click', (e) => { if (e.target === e.currentTarget) closeProfileMgmt(); });

    // Admin list modal close on backdrop
    const adminListModal = document.getElementById('adminListModal');
    if (adminListModal) adminListModal.addEventListener('click', (e) => { if (e.target === e.currentTarget) closeAdminModal(); });

    // Global: toggle body.modal-open when any modal-overlay becomes active
    const modalObserver = new MutationObserver(() => {
        const anyActive = document.querySelector('.modal-overlay.active');
        document.body.classList.toggle('modal-open', !!anyActive);
    });
    document.querySelectorAll('.modal-overlay').forEach(el => {
        modalObserver.observe(el, { attributes: true, attributeFilter: ['class'] });
    });

});

// ============================================================================
// ANALYTICS DASHBOARD
// ============================================================================
